import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi } from '@/api/client';
import { Loader2, CheckCircle } from 'lucide-react';

export function SetupPage() {
  const [step, setStep] = useState<'checking' | 'setup' | 'complete'>('checking');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [jwtSecret, setJwtSecret] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    checkSetupStatus();
  }, []);

  const checkSetupStatus = async () => {
    const response = await authApi.setupStatus();
    if (response.success && response.data?.initialized) {
      navigate('/login');
    } else {
      setStep('setup');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const response = await authApi.setup({ email, password, name, role: 'admin' });

    if (response.success && response.data) {
      setJwtSecret(response.data.suggested_jwt_secret);
      setStep('complete');
    } else {
      setError(response.error || 'セットアップに失敗しました');
    }

    setIsLoading(false);
  };

  if (step === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (step === 'complete') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
        <div className="max-w-lg w-full space-y-8">
          <div className="text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
            <h2 className="mt-4 text-2xl font-bold text-gray-900">
              セットアップ完了！
            </h2>
            <p className="mt-2 text-gray-600">
              管理者アカウントが作成されました。
            </p>
          </div>

          {jwtSecret && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="font-medium text-yellow-800 mb-2">⚠️ 重要: JWT_SECRETを設定してください</h3>
              <p className="text-sm text-yellow-700 mb-2">
                以下のシークレットを wrangler.toml または Cloudflare Dashboard の環境変数に設定してください：
              </p>
              <code className="block bg-yellow-100 p-2 rounded text-xs break-all">
                JWT_SECRET = "{jwtSecret}"
              </code>
            </div>
          )}

          <button
            onClick={() => navigate('/login')}
            className="btn-primary w-full"
          >
            ログインページへ
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h1 className="text-center text-4xl mb-2">🍚</h1>
          <h2 className="text-center text-2xl font-bold text-gray-900">
            初期セットアップ
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            最初の管理者アカウントを作成してください
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="name" className="label">名前</label>
              <input
                id="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="input"
                placeholder="管理者"
              />
            </div>

            <div>
              <label htmlFor="email" className="label">メールアドレス</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="label">パスワード</label>
              <input
                id="password"
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input"
                placeholder="8文字以上"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                作成中...
              </>
            ) : (
              '管理者アカウントを作成'
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
